import React, { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { ToolbarWithSaveHandle } from "../../components/Toolbar/ToolbarWithSave";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../Context/Contexts";
import ItemActions from "../../components/Item/ItemActions";
import { CountryAutoComplete, SupplierAutoComplete, CurrencyAutoComplete, ItemGroupAutoComplete, ProgramAutoComplete, CollectionAutoComplete, StatNoAutoComplete, GenderAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import ImageBox from "../../components/Image/ImageBox";
import Variants from "../../components/Item/Variants";
import ItemWizard from "../../components/Wizards/Item/ItemWizard"
import StockInformationDialog from "../../components/Item/StockInformationDialog";
import ItemCategoryProductGroupSelect from "../../components/Item/ItemCategoryProductGroupSelect";
import { ItemViewModel } from "../../Types/Item/ItemViewModel";
import { VariantViewModel, VariantCreate } from "../../Types/Item/VariantViewModel";
import InfoButton from "../../components/Tooltip/InfoTextField";
import {
    Box,
    Button,
    TextField,
    Typography,
    Grid,
    FormControl,
    Checkbox,
    FormControlLabel,
    Dialog,
    Switch,
    InputAdornment,
    DialogTitle,
    DialogActions,
    Stack,
    DialogContent,
    Alert
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LocalizedNumberInputs from "../../components/Input/LocalizedNumberInputs"

import CenteredCard from "../../components/Card/CenteredCardLayout";

const defaultItem: ItemViewModel = {
    no: "",
    supplierItemNo: "",
    supplierNo: "",
    description: "",
    description2: "",
    netWeight: 0,
    grossWeight: 0,
    volume: 0,
    blocked: false,
    colorGroupCode: "",
    sizeGroupCode: "",
    lengthGroupCode: "",
    itemCategoryCode: "",
    productGroupCode: "",
    itemGroupCode: "",
    programCode: "",
    collection: "",
    salesBlocked: false,
    xBusiness: false,
    xConsumer: false,
    globalDimension1Code: "",
    globalDimension2Code: "",
    skipInventory: false,
    preOrderItem: false,
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
    createdby: 0,
    updatedby: 0,
    createdByName: "",
    updatedByName: "",
    variants: [],
    img_Url: "",
    createNewItem: true
};


type EditItemProps = {
    params?: { [key: string]: any };
};

const ItemEditPage: React.FC<EditItemProps> = ({ params }) => {
    const { authService } = useAuth();
    const { goBack, navigate } = useNavigation();
    const { showSnackbar } = useSnackbar();
    // Destructure "No" from params
    const { itemNo = "" } = params || {};

    const [viewModel, setViewModel] = useState<ItemViewModel>({
        ...defaultItem,
        hasChanges: false,
        originalValues: { ...defaultItem }
    });
    const [wizardOpen, setWizardOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { showLoading, hideLoading, showThinking, hideThinking } = useLoading();
    const [pendingImage, setPendingImage] = useState<File | null>(null);
    const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
    //  const [purchaseCurrencyList, setPurchaseCurrencyList] = useState<Option[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalOpenVariants, setModalOpenVariants] = useState(false); // Variants modal
    const [isStockInformationOpen, setIsStockInformationOpen] = useState(false);
    const [itemUIValidationErrors, setItemUIValidationErrors] = useState<string[]>([]);
    const [showUserChangeInfo, setShowUserChangeInfo] = useState(false);
    const [showSuggestNewItem, setShowSuggestNewItem] = useState(false);
    const [suggestExisting, setSuggestExisting] = useState(true);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
    const [prompt, setPrompt] = useState<string>("");
    const cancelWizard = () => setWizardOpen(false);
    const toggleModalVariants = () => {
        setModalOpenVariants((prev) => !prev);
    };
    const [showSettings, setShowSettings] = useState(false);
    const { t } = useTranslation();

    const hasPendingChanges = () => viewModel.hasChanges === true;

    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };

    const preventIfUnsaved = () => {
        if (hasPendingChanges()) {
            showHasChangesError();
            return true;
        }
        return false;
    };

    const fetchItem = async (no: string) => {
        setIsLoading(true);
        try {
            const response = await authService.get<ItemViewModel>(`/itemApi/GetItem/?itemNo=${no}`);
            if (!response) {
                showSnackbar(t("dialog.itemNotFoundWithDefault", { no }), "warning");
                setViewModel({ ...defaultItem, hasChanges: false, originalValues: { ...defaultItem } });
                setWizardOpen(true);
            } else {
                setViewModel({ ...response, hasChanges: false, originalValues: { ...response } });
            }
        } catch (error: any) {
            showSnackbar(`${t("dialog.itemFetchError")} : ${error.response?.data ?? error.message}, ${t("dialog.settingNewItem")}`, "error");
            setViewModel({ ...defaultItem, hasChanges: false, originalValues: { ...defaultItem } });
            setWizardOpen(true);
        } finally {
            setIsLoading(false);
        }
    };





    const createVariant = (variant: VariantCreate): VariantViewModel => {

        // Return a properly initialized VariantViewModel object
        const newVariant: VariantViewModel = {
            id: 0,
            itemNo: viewModel.no,
            colourBaseCode: "",
            colourCode: variant.colourCode ?? "",
            sizeCode: variant.sizeCode ?? "",
            lengthCode: variant.lengthCode ?? "",
            colourSorting: null,
            sizeSorting: null,
            lengthSorting: null,
            blocked: false,
            salesBlocked: false,
            colourDescription: variant.colourDescription ?? "",
            colourBaseDescription: "",
            sizeDescription: variant.sizeDescription ?? "",
            lengthDescription: variant.lengthDescription ?? "",
            barcode: null,
            leadTimeCalc: null,
            reorderPoint: null,
            reorderQuantity: null,
            shelfNo: null,
            collection: null,
            supplierItemNo: null,
            externalReference: null,
            colourGroupCode: viewModel.createNewItem ? variant.colorGroup ?? viewModel.colorGroupCode : viewModel.colorGroupCode,
            sizeGroupCode: viewModel.createNewItem ? variant.sizeGroup ?? viewModel.sizeGroupCode : viewModel.sizeGroupCode,
            lengthGroupCode: viewModel.createNewItem ? variant.lengthGroup ?? viewModel.lengthGroupCode : viewModel.lengthGroupCode,
            created: new Date().toISOString(),
            updated: new Date().toISOString(),
            createdby: 0,
            updatedby: 0,
            netWeight: null,
            grossWeight: null,
            volume: null,
            productPostingGroupKey: null,
            externalReference2: null,
            externalReference3: null,
            purchaseBlocked: null,
            externalReference4: null,
            dimLength: null,
            dimWidth: null,
            dimHeight: null,
            statNo: null,
            isCreating: true
        };

        if (viewModel.createNewItem) {
            setViewModel((prev) => ({
                ...prev,
                colorGroupCode: newVariant.colourGroupCode || prev.colorGroupCode,
                sizeGroupCode: newVariant.sizeGroupCode || prev.sizeGroupCode,
                lengthGroupCode: newVariant.lengthGroupCode || prev.lengthGroupCode,
            }));
        }
        return newVariant;
    };




    useEffect(() => {
        if (itemNo) {
            fetchItem(itemNo);
        }
        else {
            setWizardOpen(true);
            setIsLoading(false);
        }
    }, [itemNo]);


    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);


    const setItemFromWizard = async (overrideItem: ItemViewModel) => {
        var errors = validateItem(overrideItem);
        flushSync(() => setViewModel(overrideItem)); 
        await handleSave(overrideItem, errors)
    }
    const handleSave = async (override?: ItemViewModel | null, overrideValidation?: string[]) => {
        const currentItem = override ?? viewModel
        var newItem = currentItem.createNewItem ?? false;
        const errors = overrideValidation ?? itemUIValidationErrors;
        if (errors.length > 0) {
            showSnackbar(`${t("dialog.fixErrors")}\n${errors.join("\n")}`, "error");
            return;
        }
        setIsLoading(true);
        try {

            var response = await authService.post<ItemViewModel>(`/itemApi/CreateOrUpdate`, currentItem);
            setViewModel(response ? { ...response, hasChanges: false, originalValues: { ...response } } : defaultItem);
            showSnackbar(t("dialog.itemSaved"), "success");
            if (pendingImage) {
                await uploadImage(pendingImage, response.no);
                setPendingImage(null);
            }

            // Save all variants that were stored locally
            //if (currentItem.variants?.length) {
            //    for (const variant of currentItem.variants) {
            //        await authService.post<VariantViewModel>("itemApi/CreateOrUpdateVariant", {
            //            ...variant,
            //            itemNo: response.no,
            //        });
            //    }
            //    showSnackbar(t("dialog.variantsSaved"), "success");

            //}

            if (newItem) {
                setWizardOpen(false);
                //dont === cuz sometimes it cast bool as string
                navigate({ path: "/purchase/item/edit", params: { itemNo: response.no } });

            }
        } catch (error: any) {
            console.error("Failed to save item:", error);
            showSnackbar(`${t("dialog.failedToSaveChanges")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = async () => {
        var newItem = viewModel.createNewItem ?? false;
        if (newItem) {
            showSnackbar(t("dialog.cannotCopyPreliminaryItem"), "warning");

            return;
        }
        setIsLoading(true);
        try {
            var response = await authService.post<ItemViewModel>(`/itemApi/CopyItem`, viewModel);
            setViewModel(response);
            showSnackbar(t("dialog.itemCopied"), "success");

            navigate({ path: "/purchase/item/edit", params: { itemNo: response.no } });

        } catch (error: any) {

            showSnackbar(`${t("dialog.failedToSaveChanges")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };


    const handleDelete = async () => {
        const isConfirmed = window.confirm(t("dialog.deleteItemConfirm"));
        if (!isConfirmed) return;
        var newItem = viewModel.createNewItem ?? false;
        if (newItem) {
            navigate({ path: "/purchase/item/list" });
            return;
        }

        setIsLoading(true);
        try {
            await authService.post(`/itemApi/DeleteItem`, viewModel);
            showSnackbar(t("dialog.itemDeleted"), "success");

            navigate({ path: "/purchase/item/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.itemDeleteError")} : ${error.response?.data ?? error.message}`, "error");


        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (key: keyof ItemViewModel, value: any) => {
        setViewModel((prev) => {
            const updatedItem = {
                ...prev,
                [key]: value,
                hasChanges: true
            };
            validateItem(updatedItem);
            return updatedItem;
        });
    };


    const handleChanges = (changes: Partial<ItemViewModel>) => {
        setViewModel((prev) => {
            const updatedItem = {
                ...prev,
                ...changes,
                hasChanges: true
            };
            validateItem(updatedItem);
            return updatedItem;
        });
    };

    const handleCancelChanges = () => {
        //setIsLoading(true);
        try {

            setViewModel((prev) => {
                console.log("cancelChanges", prev);
                if (!prev || !prev.originalValues) return prev;
                const original = prev.originalValues;
                const changes: Partial<ItemViewModel> = {};
                for (const key in original) {
                    const typedKey = key as keyof ItemViewModel;
                    const originalValue = original[typedKey];
                    const currentValue = prev[typedKey];
                    if (originalValue !== currentValue) {
                        (changes as any)[typedKey] = originalValue;
                    }
                }
                if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };
                const updatedItem = { ...prev, ...changes, hasChanges: false };
                validateItem(updatedItem);
                console.log("updatedItem", updatedItem);
                return updatedItem;
            });
        }
        catch (error) {
            console.log("error", error);
        }
        finally {
            // setIsLoading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return "";

        if (viewModel.createNewItem) {
            setPendingImage(file);
            setPendingImageUrl(URL.createObjectURL(file));
            return URL.createObjectURL(file);
        } else {
            uploadImage(file, viewModel.no);
        }
        return "";
    };

    const uploadImage = async (file: File, itemNo: string) => {
        try {
            setIsLoading(true);
            const formData = new FormData();
            formData.append("file", file);
            formData.append("itemNo", itemNo);

            const response = await authService.post<{ url: string }>("/itemApi/UploadImage", formData);
            if (response?.url) {
                setViewModel((prev) => ({ ...prev, img_Url: response.url }));
                showSnackbar(t("dialog.imageUploaded"), "success");

            } else {
                throw new Error("Failed to upload image.");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            showSnackbar(t("dialog.imageUploadError"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const validateItem = (updatedItem: ItemViewModel) => {
        let errors: string[] = [];

        if (updatedItem.createNewItem && /\s/.test(updatedItem.no)) {
            errors.push(t("help.itemNoNoSpaces"));
        }
        if (!updatedItem.description || updatedItem.description.length == 0) {
            errors.push(t("dialog.enterValidDescription"));
        }

        setItemUIValidationErrors(errors);
        return errors;
    };

    const addVariantsToNewItem = (variant: VariantViewModel) => {
        setViewModel((prev) => ({
            ...prev,
            variants: prev.variants?.some((v) => v.guid === variant.guid)
                ? prev.variants.map((v) => (v.guid === variant.guid ? variant : v))
                : [...(prev.variants || []), variant],
            hasChanges: true,
        }));
    };

    const removeVariantsFromNewItem = (variant: VariantViewModel) => {
        setViewModel((prev) => ({
            ...prev,
            variants: prev.variants?.filter((v) => v.guid !== variant.guid) || [],
            hasChanges: true,
        }));
    };

    const handleCreateItem = () => {
        const isConfirmed = window.confirm(t("dialog.areYouSure"));
        if (!isConfirmed) return;
        window.location.href = "/purchase/item/edit";
        navigate({ path: "/purchase/item/edit", params: { itemNo: "" } });
    };

    const suggestItem = async () => {
        if (!prompt) {
            showSnackbar(t("dialog.missingPrompt"), "warning");
            return;
        }
        try {
            showThinking();
            var response = await authService.post<ItemViewModel>("/itemApi/SuggestItemChange", { prompt, viewModel: viewModel, updateExisting: suggestExisting ?? false });
            closeSuggestDialog();
            setViewModel(prev => ({
                ...response,
                createNewItem: !suggestExisting,
                hasChanges: true,
                originalValues: suggestExisting ? prev.originalValues : { ...response }
            }));
            showSnackbar(t("common.success"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.saveError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideThinking();
        }
    };


    const closeSuggestDialog = () => {
        setShowSuggestNewItem(false);
        setPrompt("");
        setSuggestExisting(true);
    };

    return (
        <>
            <CenteredCard

                ref={toolbarRef}
                title={t("common.edit") + " " + t("common.item")}
                isLoading={isLoading}
                handleSave={handleSave}
                handleCancel={handleCancelChanges}
                hasChanges={viewModel?.hasChanges ?? false}
                fullWidth={true}
            >


                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    maxWidth: "fit-content",
                    mb: 2
                }}>
                    <ItemActions
                        onCopy={handleCopy}
                        onDelete={handleDelete}
                        onCreate={handleCreateItem}
                        stockQuickView={() => setIsStockInformationOpen(true)}
                        suggestItem={() => setShowSuggestNewItem(true)}
                        priceList={viewModel.createNewItem ? undefined : () => window.open(`/sales/pricelist/list?itemNo=${itemNo}`, "_blank")}

                    />
                </Box>
                {isLoading && <Typography>Loading...</Typography>}
                {!isLoading && (
                    <>
                        <Dialog open={wizardOpen} maxWidth="md" fullWidth>
                            <Box p={3}>
                                <ItemWizard
                                    initialItem={structuredClone(viewModel)}
                                    setItemFromWizard={setItemFromWizard}
                                    handleFileChange={handleFileChange}
                                    cancelWizard={cancelWizard}
                                />
                            </Box>
                        </Dialog>
                        <Grid container spacing={2}>
                            {/* Editable fields */}
                            <Grid item lg={6} xs={12}>
                                <TextField
                                    label={t("common.itemNo")}
                                    value={viewModel.no}
                                    onChange={(e) => handleChange("no", e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!viewModel.createNewItem} // Editable if new item
                                    margin="normal"
                                    inputProps={{ maxLength: 50 }}
                                    error={itemUIValidationErrors.includes(t("help.itemNoNoSpaces"))}
                                    helperText={
                                        itemUIValidationErrors.includes(t("help.itemNoNoSpaces"))
                                            ? t("help.itemNoNoSpaces")
                                            : ""
                                    }
                                />
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    {/* Current or Temporary Image Preview */}
                                    <ImageBox
                                        imageUrl={pendingImage ? pendingImageUrl ?? "" : viewModel.img_Url ?? ""}
                                        altText={viewModel.description}
                                        fallbackText="No Image"
                                    />

                                    {/* Image Upload Input */}
                                    <Button
                                        variant="contained"
                                        component="label"
                                        sx={{ textTransform: "none", maxWidth: "250px", mt: 2 }}
                                    >
                                        {t("common.uploadImage")}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                </Box>

                                <TextField
                                  
                                    label={t("common.description")}
                                    onChange={(e) => handleChange("description", e.target.value)}
                                    fullWidth
                                    value={viewModel.description}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ maxLength: 100 }}
                                    error={!viewModel.description || viewModel.description.trim() === ""}
                                    helperText={
                                        !viewModel.description || viewModel.description.trim() === ""
                                            ? t("help.descriptionRequired")
                                            : ""
                                    }
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.description")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                  
                                    label={t("common.description2")}
                                    value={viewModel.description2}
                                    onChange={(e) => handleChange("description2", e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ maxLength: 100 }}
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.description2")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                 
                                    label={t("common.fullDescription")}
                                    value={viewModel.fullDescription ?? ""}
                                    onChange={(e) => handleChange("fullDescription", e.target.value)}
                                    fullWidth
                                    multiline
                                    maxRows={10}
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.fullDescription")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                            
                                    label={t("common.supplierItemNo")}
                                    value={viewModel.supplierItemNo}
                                    onChange={(e) => handleChange("supplierItemNo", e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ maxLength: 100 }}
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.supplierItemNo")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormControl fullWidth margin="normal">
                                    <SupplierAutoComplete
                                        key={viewModel.supplierNo || null}
                                        valueId={viewModel.supplierNo || null}
                                        onChange={(selected) => handleChange("supplierNo", selected?.value || "")}
                                        infoText={t("help.primarySupplier")}
                                    />

                                </FormControl>
                                <TextField
                                
                                    label={t("common.barcode")}
                                    value={viewModel.barcode}
                                    onChange={(e) => handleChange("barcode", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 50 }}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.barcode")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {!viewModel.createNewItem && (
                                    <>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={<Switch checked={showUserChangeInfo} onChange={(e) => setShowUserChangeInfo(e.target.checked)} />}
                                                label={t("common.showUserChangeInfo")}
                                            />
                                        </Grid>
                                        {showUserChangeInfo && (
                                            <>
                                                <TextField
                                                    label={t("common.created")}
                                                    value={viewModel.created ? new Date(viewModel.created).toLocaleString() : ""}
                                                    fullWidth
                                                    margin="normal"
                                                    InputProps={{ readOnly: true }}
                                                    InputLabelProps={{ shrink: true }}
                                                    disabled={true}
                                                />

                                                <TextField
                                                    label={t("common.createdby")}
                                                    value={viewModel.createdByName ?? ""}
                                                    fullWidth
                                                    margin="normal"
                                                    InputProps={{ readOnly: true }}
                                                    InputLabelProps={{ shrink: true }}
                                                    disabled={true}
                                                />
                                                <TextField
                                                    label={t("common.updated")}
                                                    value={viewModel.updated ? new Date(viewModel.updated).toLocaleString() : ""}
                                                    fullWidth
                                                    margin="normal"
                                                    InputProps={{ readOnly: true }}
                                                    InputLabelProps={{ shrink: true }}
                                                    disabled={true}
                                                />
                                                <TextField
                                                    label={t("common.updatedby")}
                                                    value={viewModel.updatedByName ?? ""}
                                                    fullWidth
                                                    margin="normal"
                                                    InputProps={{ readOnly: true }}
                                                    InputLabelProps={{ shrink: true }}
                                                    disabled={true}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </Grid>
                            <Grid item lg={6} xs={12}>
                                <LocalizedNumberInputs
                                    label={t("common.salesPrice")}
                                 
                                    value={viewModel.salesPrice ?? null}
                                    onValueChange={(value) => {
                                        handleChange("salesPrice", value ?? null);
                                    }}
                                    allowNegative={false} // Prevents negative numbers
                                    decimalScale={4} // Ensures max 4 decimals
                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                    error={!viewModel.salesPrice}
                                    helperText={!viewModel.salesPrice ? t("help.salesPriceRequired") : ""}
                                />
                                {/*<TextField*/}
                                {/*    label={t("common.salesPrice")}*/}
                                {/*    value={item.salesPrice || ""}*/}
                                {/*    type="number"*/}
                                {/*    onChange={(e) => handleChange("salesPrice", parseFloat(e.target.value) || null)}*/}
                                {/*    fullWidth*/}
                                {/*    InputLabelProps={{ shrink: true }}*/}
                                {/*    margin="normal"*/}
                                {/*    error={!item.salesPrice}*/}
                                {/*    helperText={!item.salesPrice ? t("help.salesPriceRequired") : ""}*/}
                                {/*    InputProps={{*/}
                                {/*        endAdornment: (*/}
                                {/*            <InputAdornment position="end">*/}
                                {/*                <InfoButton infoText={t("help.salesPrice")} />*/}
                                {/*            </InputAdornment>*/}
                                {/*        ),*/}
                                {/*    }}*/}
                                {/*/>*/}

                                <FormControl fullWidth margin="normal">
                                    <CurrencyAutoComplete
                                        key={viewModel.salesPriceCurrencyCode || null}
                                        valueId={viewModel.salesPriceCurrencyCode || null}
                                        onChange={(selected) => handleChange("salesPriceCurrencyCode", selected?.value || "")}
                                        label={t("common.salesPriceCurrency")}
                                        infoText={t("help.salesPriceCurrency")}
                                        selectFirst={true}

                                    />

                                </FormControl>

                                {/*<TextField*/}
                                {/*    label={t("common.purchasePrice")}*/}
                                {/*    value={item.purchasePrice || ""}*/}
                                {/*    type="number"*/}
                                {/*    onChange={(e) => handleChange("purchasePrice", parseFloat(e.target.value) || null)}*/}
                                {/*    fullWidth*/}
                                {/*    error={!item.purchasePrice}*/}

                                {/*    helperText={!item.purchasePrice ? t("help.purchasePriceRequired") : ""}*/}
                                {/*    InputLabelProps={{ shrink: true }}*/}
                                {/*    margin="normal"*/}
                                {/*    InputProps={{*/}
                                {/*        endAdornment: (*/}
                                {/*            <InputAdornment position="end">*/}
                                {/*                <InfoButton infoText={t("help.purchasePrice")} />*/}
                                {/*            </InputAdornment>*/}
                                {/*        ),*/}
                                {/*    }}*/}
                                {/*/>*/}

                                <LocalizedNumberInputs
                                    label={t("common.purchasePrice")}
                                  
                                    value={viewModel.purchasePrice ?? null}
                                    onValueChange={(value) => {
                                        handleChange("purchasePrice", value ?? null);
                                    }}
                                    allowNegative={false} // Prevents negative numbers
                                    decimalScale={4} // Ensures max 4 decimals
                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                    error={!viewModel.purchasePrice}
                                    helperText={!viewModel.purchasePrice ? t("help.purchasePriceRequired") : ""}
                                />

                                <FormControl fullWidth margin="normal">
                                    <CurrencyAutoComplete
                                        key={viewModel.purchasePriceCurrencyCode || null}
                                        valueId={viewModel.purchasePriceCurrencyCode || null}
                                        onChange={(selected) => handleChange("purchasePriceCurrencyCode", selected?.value || "")}
                                        label={t("common.purchasePriceCurrency")}
                                        infoText={t("help.purchasePriceCurrency")}
                                        selectFirst={true}
                                    />
                                </FormControl>
                                <ItemCategoryProductGroupSelect
                                    key={`${viewModel?.itemCategoryCode ?? ""}::${viewModel?.productGroupCode ?? ""}`}
                                    itemCategoryCode={viewModel.itemCategoryCode}
                                    productGroupCode={viewModel.productGroupCode}
                                    onChange={(cat, group) =>
                                        handleChanges({ itemCategoryCode: cat, productGroupCode: group })
                                    }
                                />

                                <FormControl fullWidth margin="normal">
                                    <ItemGroupAutoComplete
                                        key={viewModel.itemGroupCode || null}
                                        valueId={viewModel.itemGroupCode || null}
                                        onChange={(selected) => handleChange("itemGroupCode", selected?.value || "")}

                                    />
                                </FormControl>

                                <FormControl fullWidth margin="normal">

                                    <ProgramAutoComplete
                                        key={viewModel.programCode || null}
                                        valueId={viewModel.programCode || null}
                                        onChange={(selected) => handleChange("programCode", selected?.value || "")}

                                    />
                                </FormControl>

                                <FormControl fullWidth margin="normal">
                                    <CollectionAutoComplete
                                        key={viewModel.collection || null}
                                        valueId={viewModel.collection || null}
                                        onChange={(selected) => handleChange("collection", selected?.value || "")}

                                    />
                                </FormControl>

                                <FormControl fullWidth margin="normal">
                                    <GenderAutoComplete
                                        key={viewModel.gender || null}
                                        valueId={viewModel.gender || null}
                                        onChange={(selected) => handleChange("gender", selected?.value || "")}

                                    />
                                </FormControl>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />}
                                        label={t("common.showCustoms")}
                                    />
                                </Grid>

                            </Grid>



                            {/* Settings Section */}
                            {showSettings && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            backgroundColor: "secondary.main",
                                            padding: 2,
                                            borderRadius: 2,
                                            width: "100%"
                                        }}
                                    >

                                        {/*<TextField*/}
                                        {/*    label={t("common.netWeight") + " (g)"}*/}
                                        {/*    value={item.netWeight}*/}
                                        {/*    type="number"*/}
                                        {/*    onChange={(e) =>*/}
                                        {/*        handleChange('netWeight', parseFloat(e.target.value) || 0)*/}
                                        {/*    }*/}
                                        {/*    fullWidth*/}
                                        {/*    margin="normal"*/}
                                        {/*    InputProps={{*/}
                                        {/*        endAdornment: (*/}
                                        {/*            <InputAdornment position="end">*/}
                                        {/*                <InfoButton infoText={t("help.netWeight")} />*/}
                                        {/*            </InputAdornment>*/}
                                        {/*        ),*/}
                                        {/*    }}*/}
                                        {/*/>*/}

                                        <LocalizedNumberInputs
                                            label={t("common.netWeight") + " (g)"}
                                            value={viewModel.netWeight ?? null}
                                            onValueChange={(value) => {
                                                handleChange("netWeight", value ?? null);
                                            }}
                                            allowNegative={false} // Prevents negative numbers
                                            decimalScale={4} // Ensures max 4 decimals
                                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                        />


                                        <LocalizedNumberInputs
                                            label={t("common.grossWeight") + " (g)"}
                                            value={viewModel.grossWeight ?? null}
                                            onValueChange={(value) => {
                                                handleChange("grossWeight", value ?? null);
                                            }}
                                            allowNegative={false} // Prevents negative numbers
                                            decimalScale={4} // Ensures max 4 decimals
                                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                        />
                                        {/*<TextField*/}
                                        {/*    label={t("common.grossWeight") + " (g)"}*/}
                                        {/*    value={item.grossWeight}*/}
                                        {/*    type="number"*/}
                                        {/*    onChange={(e) =>*/}
                                        {/*        handleChange('grossWeight', parseFloat(e.target.value) || 0)*/}
                                        {/*    }*/}
                                        {/*    fullWidth*/}
                                        {/*    margin="normal"*/}
                                        {/*    InputProps={{*/}
                                        {/*        endAdornment: (*/}
                                        {/*            <InputAdornment position="end">*/}
                                        {/*                <InfoButton infoText={t("help.grossWeight")} />*/}
                                        {/*            </InputAdornment>*/}
                                        {/*        ),*/}
                                        {/*    }}*/}
                                        {/*/>*/}
                                        {/*<TextField*/}
                                        {/*    label={t("common.volume")}*/}
                                        {/*    value={item.volume}*/}
                                        {/*    type="number"*/}
                                        {/*    onChange={(e) =>*/}
                                        {/*        handleChange('volume', parseFloat(e.target.value) || 0)*/}
                                        {/*    }*/}
                                        {/*    fullWidth*/}
                                        {/*    margin="normal"*/}
                                        {/*    InputProps={{*/}
                                        {/*        endAdornment: (*/}
                                        {/*            <InputAdornment position="end">*/}
                                        {/*                <InfoButton infoText={t("help.volume")} />*/}
                                        {/*            </InputAdornment>*/}
                                        {/*        ),*/}
                                        {/*    }}*/}
                                        {/*/>*/}

                                        <LocalizedNumberInputs
                                            label={t("common.volume")}
                                            value={viewModel.volume ?? null}
                                            onValueChange={(value) => {
                                                handleChange("volume", value ?? null);
                                            }}
                                            allowNegative={false} // Prevents negative numbers
                                            decimalScale={4} // Ensures max 4 decimals
                                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                        />
                                        <FormControl fullWidth margin="normal">
                                            <StatNoAutoComplete
                                                key={viewModel.statNo || null}
                                                valueId={viewModel.statNo || null}
                                                load={true}
                                                onChange={(selected) => handleChange("statNo", selected?.value || "")}
                                                infoText={t("help.statNo")}
                                            />

                                        </FormControl>
                                        <FormControl fullWidth margin="normal">
                                            <CountryAutoComplete
                                                key={viewModel.originCountryCode || null}
                                                valueId={viewModel.originCountryCode || null}
                                                onChange={(selected) => handleChange("originCountryCode", selected?.value || "")}
                                                label={t("common.countryOfOrigin")}
                                                infoText={t("help.countryOfOrigin")}
                                            />

                                        </FormControl>

                                    </Box>
                                </Grid>
                            )}
                            {/* Additional fields */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={viewModel.blocked}
                                            onChange={(e) => handleChange("blocked", e.target.checked)}
                                        />
                                    }
                                    label={t("common.blocked")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={viewModel.salesBlocked}
                                            onChange={(e) => handleChange("salesBlocked", e.target.checked)}
                                        />
                                    }
                                    label={t("common.salesBlocked")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={viewModel.xConsumer}
                                            onChange={(e) => handleChange("xConsumer", e.target.checked)}
                                        />
                                    }
                                    label="B2C"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={viewModel.xBusiness}
                                            onChange={(e) => handleChange("xBusiness", e.target.checked)}
                                        />
                                    }
                                    label="B2B"
                                />

                                {(viewModel.createNewItem === true || (viewModel.variants?.length ?? 0) > 0) && (
                                    <Button variant="contained" onClick={toggleModalVariants} sx={{ marginRight: 2 }}>
                                        {t("common.manageVariants")}
                                    </Button>
                                )}
                            </Grid>

                            <Dialog
                                open={modalOpenVariants}
                                onClose={toggleModalVariants}
                                fullWidth
                                maxWidth="lg"
                                PaperProps={{ sx: { borderRadius: "8px", maxHeight: "80vh", overflow: "auto" } }}
                            >
                                <Box sx={{ p: 4 }}>
                                    <Variants
                                        initItem={viewModel}
                                        initialVariants={viewModel.variants}
                                        createVariant={createVariant}
                                        isCreatingNewItem={viewModel.createNewItem ?? false}
                                        addVariantsToNewItem={addVariantsToNewItem}
                                        removeVariantsFromNewItem={removeVariantsFromNewItem}
                                    />
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                        <Button variant="contained" onClick={toggleModalVariants}>
                                            {t("common.close")}
                                        </Button>
                                    </Box>
                                </Box>
                            </Dialog>
                        </Grid>

                        {/* Buttons */}
                        <Box sx={{ marginTop: 2 }}>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleSave(null)}
                                disabled={isLoading}
                                sx={{ marginRight: 2 }}
                            >
                                {t("common.save")}
                            </Button>
                            <Button variant="outlined" onClick={goBack}>
                                {t("common.back")}
                            </Button>
                        </Box>
                    </>
                )}

            </CenteredCard>
            <StockInformationDialog
                open={isStockInformationOpen}
                onClose={() => setIsStockInformationOpen(false)}
                itemNo={viewModel.no || undefined}
            />
            <Dialog open={showSuggestNewItem} onClose={() => setShowSuggestNewItem(false)} maxWidth="md" fullWidth>
                <DialogTitle>{t("common.aiSuggest")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="info" sx={{ mb: 1 }}>
                            {t("help.suggestItemHelp")}
                        </Alert>

                        <TextField
                            label={t("common.request")}
                            value={prompt ?? ""}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => setPrompt(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                        />

                        <Alert severity="info" sx={{ mb: 1 }}>
                            {t("help.updateExisting")}
                        </Alert>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={suggestExisting}
                                    onChange={(e) => setSuggestExisting(e.target.checked)}
                                />
                            }
                            label={t("common.updateExisting")}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSuggestNewItem(false)}>{t("common.cancel")}</Button>
                    <Button onClick={suggestItem} color="success" variant="contained">
                        {t("common.run")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ItemEditPage;
